/**
 * Error codes, hand-written helpers around a generated manifest (ADR 0013).
 *
 * The backend owns the list. `backend/internal/apicode` is the only place a
 * code string is written; `go generate ./internal/apicode` renders
 * `./errors.generated.ts`, and a Go test fails when the committed file is
 * stale. **Nothing here lists a backend code** — this module only re-exports
 * the generated manifest and adds the client-only codes, which have no wire
 * representation because they are synthesised in the browser.
 *
 * Adding a code is a backend change: registry row → `go generate` → classify it
 * in `retry.ts` (whose test fails until you do) → commit both.
 */

export {
  ApiErrorCodes,
  API_ERROR_CODE_META,
  API_ERROR_CODE_LIST,
} from './errors.generated';
export type { ApiErrorCode, ApiErrorRetryClass } from './errors.generated';

import { ApiErrorCodes } from './errors.generated';
import type { ApiErrorCode } from './errors.generated';

/**
 * Codes synthesised client-side when the failure never produced a backend
 * envelope (transport failure, non-JSON body, ...).
 */
export const ClientErrorCodes = {
  /** fetch() itself rejected: offline, DNS, CORS. */
  Network: 'network_error',
  /** Non-2xx response whose body was missing or not an error envelope. */
  RequestFailed: 'request_failed',
  /** Last-resort fallback. */
  Unknown: 'unknown_error',
} as const;

export type ClientErrorCode = (typeof ClientErrorCodes)[keyof typeof ClientErrorCodes];

/**
 * What `ApiError.code` is typed as: any known code, but still assignable from
 * an arbitrary string so a newly added backend code never breaks the build.
 * The `string & {}` arm keeps editor completion for the known members.
 */
export type ApiErrorCodeLike = ApiErrorCode | ClientErrorCode | (string & {});

/**
 * The four 401s that mean "this request is invalid; the session is fine".
 *
 * Doc 10 §1.8's rule — "a 401 means no usable session" — is amended for
 * exactly these (docs/plan/14 §5.4): the response leaves the cookie trio in
 * place and the record alive, because a captured cookie must never become a
 * remote logout. Treating one as a sign-out would throw away a live session, a
 * warm cache and whatever the user was half-way through writing, over a
 * missing *device key*. The transport therefore dispatches on the code before
 * the generic 401 path, and the recovery dialog handles it in place.
 *
 * `stream_ticket_invalid` is deliberately not here: it is refused on its own
 * route and answered by minting another ticket (ADR 0041), never by a dialog.
 */
export const PROOF_FAILURE_CODES: ReadonlySet<string> = new Set<string>([
  ApiErrorCodes.ProofRequired,
  ApiErrorCodes.ProofInvalid,
  ApiErrorCodes.ProofStale,
  ApiErrorCodes.ProofReplayed,
]);

/** True for a 401 that must not sign the user out. */
export function isProofFailureCode(code: ApiErrorCodeLike): boolean {
  return typeof code === 'string' && PROOF_FAILURE_CODES.has(code);
}

const BACKEND_CODES: ReadonlySet<string> = new Set(Object.values(ApiErrorCodes));

/** Narrows an arbitrary string to a backend-defined error code. */
export function isApiErrorCode(value: unknown): value is ApiErrorCode {
  return typeof value === 'string' && BACKEND_CODES.has(value);
}
